import { useEffect, useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import type { GalleryPost } from "@/graphql";
import { env } from "@/lib/env";

export interface PreviewBodyButton {
  url: string;
  text: string;
  enabled: boolean;
}

export interface PreviewBodyStyle {
  buttonRadius?: number | null;
  buttonBorder?: number | null;
  buttonBorderColour?: string | null;
  buttonFontColour?: string | null;
  buttonBackground?: string | null;
  buttonOpacity?: number | null;
  buttonBoxShadowColour?: string | null;
  background?: string | null;
  fontColour?: string | null;
}

export interface PreviewBodyData {
  instagramUsername: string;
  instagramProfileImageUrl: string | null;
  hasProfileHeader: boolean;
  hasIntroduction: boolean;
  introductionText: string | null;
  buttons: PreviewBodyButton[];
  hasGallery: boolean;
  galleryTitle: string | null;
  galleryId: string | null;
  /** Draft only — published previews don't expose redirect mode. */
  hasRedirect?: boolean;
  galleryRedirect?: boolean;
  /** Draft only — design fields. Published previews use the published style. */
  style?: PreviewBodyStyle | null;
  css?: string | null;
}

interface PreviewBodyProps {
  data: PreviewBodyData;
  /** Show the "redirect mode is on" notice when applicable (draft only). */
  showRedirectNotice?: boolean;
}

/**
 * Renders the actual Sked Link preview body using the legacy class names
 * (.container, .button, .gallery, etc.). The legacy CSS lives in
 * styles-css/preview.css and customer-supplied CSS may override it.
 */
export function PreviewBody({
  data,
  showRedirectNotice = false,
}: PreviewBodyProps) {
  const [galleryData, setGalleryData] = useState<GalleryResponse | null>(null);
  const [galleryFetchFailed, setGalleryFetchFailed] = useState(false);

  useEffect(() => {
    if (!data.galleryId || !data.hasGallery) {
      setGalleryData(null);
      setGalleryFetchFailed(false);
      return;
    }

    const controller = new AbortController();

    async function loadGallery() {
      try {
        setGalleryFetchFailed(false);
        const response = await fetch(
          `${env.schedugramServerBackendUrl ?? "http://localhost:3000/"}public/${data.galleryId}/feed`,
          {
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(
            `Gallery fetch failed with status ${response.status}`
          );
        }

        const payload = (await response.json()) as GalleryResponse;
        if (payload.status !== "ok") {
          throw new Error("Gallery response was not ok");
        }

        setGalleryData(payload);
      } catch (error) {
        if (controller.signal.aborted) return;
        setGalleryData(null);
        setGalleryFetchFailed(true);
      }
    }

    void loadGallery();

    return () => {
      controller.abort();
    };
  }, [data.galleryId, data.hasGallery]);

  const style = data.style ?? {};
  const profileImageUrl =
    galleryData?.data?.profile_picture_url ??
    data.instagramProfileImageUrl ??
    "";
  const galleryPosts: GalleryPost[] = galleryData?.data?.posts ?? [];
  const galleryMissingConfiguration = data.hasGallery && !data.galleryId;

  const inRedirectMode =
    showRedirectNotice &&
    (data.hasRedirect === true || data.galleryRedirect === true);

  if (inRedirectMode) {
    return (
      <section
        className="container"
        style={{
          backgroundColor: style.background ?? "#ffffff",
          color: style.fontColour ?? "#000000",
          height: "100%",
          paddingTop: "32px",
          paddingLeft: "10px",
          paddingRight: "5px",
        }}
      >
        <h3 style={{ paddingTop: "20px" }}>
          You have turned on the redirect feature — no page will be shown
        </h3>
        <p style={{ fontSize: "16px", color: "black" }}>
          When you enable the &quot;Redirect to latest post&quot; or
          &quot;Redirect&quot; features, we will send visitors to the latest
          post URL or the provided URL rather than your Sked Link page.
        </p>
        <p style={{ fontSize: "16px", color: "black" }}>
          Disable these feature(s) to instead show visitors a page with buttons
          or a gallery.
        </p>
      </section>
    );
  }

  return (
    <section
      className="container"
      style={{
        backgroundColor: style.background ?? "#ffffff",
        color: style.fontColour ?? "#000000",
        height: "100%",
        paddingTop: "32px",
      }}
    >
      {data.hasProfileHeader && (
        <div className="profile-header">
          <img
            className="profile-image profile-image-circle"
            src={profileImageUrl}
            alt={`Profile of ${data.instagramUsername}`}
          />
          <h3 className="profile-username">@{data.instagramUsername}</h3>
        </div>
      )}
      {data.hasIntroduction && data.introductionText ? (
        <div className="introduction">
          <p>{data.introductionText}</p>
        </div>
      ) : null}
      <div className="buttons">
        {data.buttons.map((button, index) => {
          if (!button.enabled) return null;
          return (
            <div
              key={index}
              className="button"
              style={{
                borderRadius: style.buttonRadius ?? undefined,
                borderWidth: style.buttonBorder ?? undefined,
                borderColor: style.buttonBorderColour ?? undefined,
                backgroundColor: style.buttonBackground ?? undefined,
                opacity:
                  style.buttonOpacity != null
                    ? style.buttonOpacity / 100
                    : undefined,
                boxShadow: style.buttonBoxShadowColour
                  ? `6px 7px 0px 0px ${style.buttonBoxShadowColour}`
                  : undefined,
              }}
            >
              <a
                href={button.url}
                style={{ color: style.buttonFontColour ?? undefined }}
              >
                {button.text}
              </a>
            </div>
          );
        })}
      </div>
      {data.hasGallery && (
        <div className="gallery">
          {data.galleryTitle ? (
            <h3 className="gallery-title">{data.galleryTitle}</h3>
          ) : null}
          {galleryMissingConfiguration ? (
            <Alert variant="destructive">
              <AlertDescription>
                Your gallery is enabled but no account key has been configured
                yet. Add the Sked account key to load gallery media.
              </AlertDescription>
            </Alert>
          ) : null}
          {galleryFetchFailed ? (
            <Alert variant="destructive">
              <AlertDescription>
                Your gallery is enabled but the provided key is not returning
                any data. Please check the account key.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="row">
            {galleryPosts.map((post, index) => (
              <div key={index} className="cellContainer">
                <a className="cell" href={post.data.redirectUrl ?? "#"}>
                  <img src={post.data.thumbnailUrl} alt="" />
                </a>
                {post.data.redirectUrl ? (
                  <svg
                    className="link-icon"
                    data-icon="link"
                    viewBox="0 0 16 16"
                    style={{ fill: "white" }}
                  >
                    <title>link</title>
                    <path
                      d="M4.99 11.99c.28 0 .53-.11.71-.29l6-6a1.003 1.003 0 0 0-1.42-1.42l-6 6a1.003 1.003 0 0 0 .71 1.71zm3.85-2.02L6.4 12.41l-1 1-.01-.01c-.36.36-.85.6-1.4.6-1.1 0-2-.9-2-2 0-.55.24-1.04.6-1.4l-.01-.01 1-1 2.44-2.44c-.33-.1-.67-.16-1.03-.16-1.1 0-2.09.46-2.81 1.19l-.02-.02-1 1 .02.02c-.73.72-1.19 1.71-1.19 2.81 0 2.21 1.79 4 4 4 1.1 0 2.09-.46 2.81-1.19l.02.02 1-1-.02-.02c.73-.72 1.19-1.71 1.19-2.81 0-.35-.06-.69-.15-1.02zm7.15-5.98c0-2.21-1.79-4-4-4-1.1 0-2.09.46-2.81 1.19l-.02-.02-1 1 .02.02c-.72.72-1.19 1.71-1.19 2.81 0 .36.06.69.15 1.02l2.44-2.44 1-1 .01.01c.36-.36.85-.6 1.4-.6 1.1 0 2 .9 2 2 0 .55-.24 1.04-.6 1.4l.01.01-1 1-2.43 2.45c.33.09.67.15 1.02.15 1.1 0 2.09-.46 2.81-1.19l.02.02 1-1-.02-.02a3.92 3.92 0 0 0 1.19-2.81z"
                      fillRule="evenodd"
                    ></path>
                  </svg>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

interface GalleryResponse {
  status: string;
  data: {
    posts: GalleryPost[];
    profile_picture_url?: string | null;
    username?: string | null;
  } | null;
}
